import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, skip } from 'rxjs/operators';
import { MainTableFilterModulesEnum } from 'src/app/core/enums/main-table-filter-modules.enum';
import { UserNonConformancesRoutes } from 'src/app/core/enums/routes.enum';
import { DashboardDepartmentChangeService } from 'src/app/core/services/dashboard-department-change.service';
import { ClearFilterDetails } from 'src/app/core/state/main-table-filter.actions';
import { NonConformanceStatus } from 'src/app/non-conformance-user/enums/non-conformance-status.enum';
import { INonConformanceReportsStatusResponse } from 'src/app/non-conformance-user/models/non-conformance-reports-status-response.model';
import {
  LoadOpenReportsForModal,
  LoadReportsStatus,
} from 'src/app/non-conformance-user/state/non-conformance.actions';
import { NonConformanceState } from 'src/app/non-conformance-user/state/non-conformance.state';
import { AlertStatus } from 'src/app/risk-assessment-user/enums/alert-status.enum';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { DashboardListModalComponent } from 'src/app/shared/dashboard/deviations-over-time/dashboard-list-modal/dashboard-list-modal.component';
import { TimePeriod } from 'src/app/shared/dashboard/models/time-period.enum';
import { getDashboardDateRange } from 'src/app/shared/functions/get-dashboard-date-range';
import { MainTableFilterOperand } from 'src/app/shared/main-table-module/main-table/main-table-column-filter/main-table-column-filter.component';
import { IdName } from 'src/app/shared/models/id-name';
import {
  MainTableFilterModel,
  MainTableFilterSearchByModel,
} from 'src/app/shared/models/main-table-filter.model';
import { WidgetBlock } from 'src/app/shared/ui-elements/ui-widget-card/ui-widget-card.component';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import { Privileges } from 'src/app/core/privileges/privileges';

const MODAL_PAGE_SIZE = 100;

@Component({
  selector: 'app-reports-dashboard-widgets',
  templateUrl: './reports-dashboard-widgets.component.html',
  styleUrls: ['./reports-dashboard-widgets.component.scss'],
  standalone: false,
})
export class ReportsDashboardWidgetsComponent implements OnInit, OnDestroy {
  createdReports = 0;
  openReports = 0;
  completedReports = 0;
  closedReports = 0;
  reportRoutes = UserNonConformancesRoutes;

  expiredReportsData: WidgetBlock[] = [];
  dueSoonReportsData: WidgetBlock[] = [];

  loadingActions = [LoadReportsStatus];

  // Loading states for individual widgets
  openReportsLoading = false;
  dueSoonReportsLoading = false;
  expiredReportsLoading = false;

  vesselControl = new FormControl<string>('', { nonNullable: true });
  vesselOptions = select(TenantUserAccessState.getAllAvailableDepartments);

  periodControl = new FormControl(TimePeriod.Last30Days);
  customRangeControl = new FormControl<{ from: Date; to: Date } | null>(null);

  private subscription = new Subscription();

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private dashboardDepartmentChangeService: DashboardDepartmentChangeService,
    private tenantUserPrivileges: TenantUserPrivilegesService
  ) {}

  ngOnInit(): void {
    this.vesselControl.setValue(getDepartmentId(), { emitEvent: false });
    this.listenToStatusChanges();
    this.loadReportsCounts();

    this.subscription.add(
      this.vesselControl.valueChanges.subscribe((departmentId) => {
        this.dashboardDepartmentChangeService.setDashboardDepartment(departmentId);
        this.loadReportsCounts();
      })
    );

    this.subscription.add(
      this.periodControl.valueChanges.subscribe((period) => {
        if (period === TimePeriod.Custom) {
          return;
        }

        this.clearCustomRangeFilters();
        this.loadReportsCounts();
      })
    );

    this.subscription.add(
      this.customRangeControl.valueChanges.subscribe((range) => {
        this.handleCustomRangeChange(range);
      })
    );

    // Reload data when tenant changes
    this.subscription.add(
      this.store
        .select(TenantUserAccessState.currentTenant)
        .pipe(
          filter((tenant) => !!tenant),
          distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
          skip(1) // Skip the initial emission to avoid duplicate loads on component init
        )
        .subscribe(() => {
          // Reset filters when tenant changes
          this.vesselControl.setValue(getDepartmentId(), { emitEvent: false });
          this.periodControl.setValue(TimePeriod.Last30Days, { emitEvent: false });
          this.clearCustomRangeFilters();
          // Note: Data reload happens in department change handler (tenant change triggers department change)
        })
    );

    // Reload data when department changes
    this.subscription.add(
      this.dashboardDepartmentChangeService.getDepartmentChanges().subscribe((departmentId) => {
        this.syncDepartment(departmentId);
      })
    );

    this.subscription.add(
      this.dashboardDepartmentChangeService
        .getDashboardDepartmentChanges()
        .subscribe((departmentId) => {
          this.syncDepartment(departmentId);
        })
    );
  }

  private listenToStatusChanges(): void {
    this.subscription.add(
      this.store
        .select(NonConformanceState.getReportsStatus)
        .subscribe((status: INonConformanceReportsStatusResponse | undefined) => {
          if (!status) {
            return;
          }

          this.updateWidgetData(status);
        })
    );
  }

  private syncDepartment(departmentId: string): void {
    if (this.vesselControl.value === departmentId) {
      return;
    }

    this.vesselControl.setValue(departmentId, { emitEvent: false });
    this.loadReportsCounts();
  }

  private updateWidgetData(status: INonConformanceReportsStatusResponse): void {
    this.createdReports = status.totalReports;
    this.openReports = status.allReportsInProgress;
    this.completedReports = status.completedReports;
    this.closedReports = status.closedReports;

    this.expiredReportsData = [
      {
        name: 'myExpiredReports',
        value: status.myReportsExpired,
        tooltip: 'myExpiredReportsTooltip',
      },
      {
        name: 'allExpiredReports',
        value: status.allReportsExpired,
        icon: 'error_outline',
        iconColor: 'red',
        tooltip: 'allExpiredReportsTooltip',
      },
    ];

    this.dueSoonReportsData = [
      {
        name: 'myDueSoonReports',
        value: status.myReportsExpiring,
        tooltip: 'myDueSoonReportsTooltip',
      },
      {
        name: 'allDueSoonReports',
        value: status.allReportsExpiring,
        icon: 'warning_amber',
        iconColor: 'yellow',
        tooltip: 'allDueSoonReportsTooltip',
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.store.dispatch(new ClearFilterDetails());
  }

  private handleCustomRangeChange(range: { from: Date; to: Date } | null): void {
    if (range) {
      this.periodControl.setValue(TimePeriod.Custom, { emitEvent: false });
      this.loadReportsCounts();
    }
  }

  private clearCustomRangeFilters(): void {
    this.customRangeControl.setValue(null, { emitEvent: false });
  }

  private loadReportsCounts(): void {
    if (!this.tenantUserPrivileges.userHasPrivilege(Privileges.NonConformanceRead)) {
      return;
    }

    const departmentId = this.vesselControl.value;
    const dateRange = getDashboardDateRange(
      this.periodControl.value || TimePeriod.Last30Days,
      this.customRangeControl.value
    );

    this.store.dispatch(new LoadReportsStatus([departmentId], dateRange.from, dateRange.to));
  }

  openExpiredReportsInOpen(): void {
    this.expiredReportsLoading = true;
    this.openReportsModal(
      NonConformanceStatus.Open,
      AlertStatus.Expired,
      'expiredReportsInOpenState'
    );
  }

  openExpiredReportsInCompleted(): void {
    this.expiredReportsLoading = true;
    this.openReportsModal(
      NonConformanceStatus.Completed,
      AlertStatus.Expired,
      'expiredReportsInCompletedState'
    );
  }

  openDueSoonReportsInOpen(): void {
    this.dueSoonReportsLoading = true;
    this.openReportsModal(
      NonConformanceStatus.Open,
      AlertStatus.Expiring,
      'dueSoonReportsInOpenState'
    );
  }

  openDueSoonReportsInCompleted(): void {
    this.dueSoonReportsLoading = true;
    this.openReportsModal(
      NonConformanceStatus.Completed,
      AlertStatus.Expiring,
      'dueSoonReportsInCompletedState'
    );
  }

  navigateToOpenReports(): void {
    this.openReportsLoading = true;
    this.openReportsModal(NonConformanceStatus.Open, undefined, 'openReportsTitle');
  }

  private openReportsModal(
    status: NonConformanceStatus,
    alertStatus: AlertStatus | undefined,
    titleKey: string
  ): void {
    const reportsFilter = this.createFilter(MODAL_PAGE_SIZE, status, alertStatus);

    this.store.dispatch(new LoadOpenReportsForModal(reportsFilter, false)).subscribe(() => {
      this.openReportsLoading = false;
      this.dueSoonReportsLoading = false;
      this.expiredReportsLoading = false;

      this.dialog.open(DashboardListModalComponent, {
        data: {
          period: '',
          items: this.store.selectSnapshot(NonConformanceState.getOpenReportsModalData),
          titleKey,
          dateField: 'nonConformanceDate',
          sortByDate: false,
        },
      });
    });
  }

  private createFilter(
    pageSize: number,
    status: NonConformanceStatus,
    alertStatus?: AlertStatus
  ): MainTableFilterModel {
    const reportsFilter = new MainTableFilterModel(MainTableFilterModulesEnum.NonConformances);
    reportsFilter.pageSize = pageSize;
    reportsFilter.pageIndex = 0;
    reportsFilter.additional = status;
    reportsFilter.orderBy = 'AlertStatus';
    reportsFilter.ascDesc = 'Descending';

    const searchByFilters: MainTableFilterSearchByModel[] = [];

    // Add alert status filter if provided
    if (alertStatus !== undefined) {
      searchByFilters.push(
        new MainTableFilterSearchByModel(
          'AlertStatus',
          [alertStatus.toString()],
          MainTableFilterOperand.In
        )
      );
    }

    // Add department filter if specific departments are selected
    const selectedDepartmentId = this.vesselControl.value;
    if (selectedDepartmentId) {
      const vessels =
        this.store.selectSnapshot(TenantUserAccessState.getAllAvailableDepartments) || [];
      const selectedVesselNames = vessels
        .filter((v: IdName) => v.id === selectedDepartmentId)
        .map((v: IdName) => v.name);

      if (selectedVesselNames.length > 0) {
        searchByFilters.push(
          new MainTableFilterSearchByModel(
            'Department.Name',
            selectedVesselNames,
            MainTableFilterOperand.In
          )
        );
      }
    }

    if (searchByFilters.length > 0) {
      reportsFilter.searchBy = searchByFilters;
    }

    return reportsFilter;
  }
}
